import * as React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Layout from "../../Components/layouts";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import { Button, CircularProgress, Paper, Typography } from '@material-ui/core';
import { CalendarServices } from '../../Services/CalendarServices';
import QueryBuilderIcon from '@material-ui/icons/QueryBuilder';
import FullCalendar from './components/FullCalendar'
import CalendarSidebar from './components/CalendarSidebar';
import EventModal from './components/EventModal'
import { notify, checkCurrentEventTime } from '../../utils';
import './calender.css';
import SingleEventModal, { calculateDuration } from './components/SingleEventModal';
import commonStyles from 'theme/commonStyles';
import SearchBoxInput from 'Components/SearchBoxInput';
import moment from 'moment';

const calendarServices = new CalendarServices();

function renderEventContent(eventInfo) {
    return (
        <>
            <b>{eventInfo.timeText}</b>
            <i>{eventInfo.event.title}</i>
        </>
    );
}

const useStyles = makeStyles((theme) => ({
    flexToppx: {
        marginBottom: 24,
        borderBottom: '1px solid #d6d6d6',
        paddingBottom: 24,
    },
    btn: {
        color: '#3D7DDD',
        border: '1px solid #3D7DDD',
        padding: '11px 25px',
        borderRadius: 15,
        background: '#fff',
        "&:hover": {
            border: "1px solid #3D7DDD",
            background: "#3D7DDD",
            color: "#fff"
        },
        [theme.breakpoints.down('xs')]: {
            padding: '8px 16px',
            fontSize: 13,
        },
    },
    headerTop: {
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    paper: {
        padding: theme.spacing(2),
        margin: "20px 0px",
        color: theme.palette.text.secondary,
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1.5),
            margin: "12px 0px",
        },
    },
    eventRoot: {
        flexGrow: 1,
    },
    cal: {
        textAlign: 'center',
        background: '#FFFFFF',
        boxShadow: '0px 4px 30px rgb(1 2 0 / 9%)',
        borderRadius: 10,
        marginTop: 20,
        [theme.breakpoints.down('xs')]: {
            marginTop: 12,
        },
    },
    date: {
        padding: '15px 10px',
        color: '#000',
        margin: 0,
        fontSize: 22,
        fontWeight: 900,
        [theme.breakpoints.down('xs')]: {
            fontSize: 18,
            padding: '10px 8px',
        },
    },
    eventDateFilter: {
        border: "1px solid #808080",
        borderRadius: 25,
        padding: '12px 18px',
    },
    calendarView: {},
    intro: {
        '& h3': {
            wordBreak: 'break-word',
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
        },
    },
    notes: {
        '& h3': {
            fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
        },
        '& p': {
            wordBreak: 'break-word',
        },
    },
    calendarMain: {
        [theme.breakpoints.down('md')]: {
            order: 1,
        },
    },
    sidebarWrap: {
        [theme.breakpoints.down('md')]: {
            order: 2,
        },
    },
    contentWrapper: {
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1.5),
        },
    },
    fullCalendarWrapper: {
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        minHeight: 400,
        [theme.breakpoints.down('xs')]: {
            minHeight: 350,
        },
    },
    eventsListSection: {
        marginTop: 20,
        [theme.breakpoints.down('xs')]: {
            marginTop: 12,
        },
    },
}));

const Header = ({ selectedDate, handleDateChange, onClickMonth }) => {
    const classes = useStyles();
    
    return (
        <div className={classes.headerTop}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DatePicker
                    variant="inline"
                    inputVariant="outlined"
                    size="small"
                    openTo="month"
                    minDate={new Date()}
                    views={["year", "month"]}
                    value={selectedDate}
                    onChange={handleDateChange}
                    style={{ maxWidth: 200 }}
                />
            </MuiPickersUtilsProvider>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <Button className={classes.btn} onClick={onClickMonth} variant="outlined">Month</Button>
                <Button 
                    className={classes.btn}
                    style={{
                        background: "#3D7DDD",
                        color: "#fff",
                        border: "1px solid #3D7DDD"
                    }}
                    variant="outlined">
                    Events
                </Button>
            </div>
        </div>
    )
}

const SearchAndExport = ({ handleSearch, showCalendar, search }) => {
    const classes = useStyles();
    return (
        <>
            <Grid container justifyContent="space-between" alignItems="center" className={classes.flexToppx} spacing={2}>
                <Grid item xs={12} sm>
                    {!showCalendar && <SearchBoxInput
                        label="Search Events"
                        placeholder="Search by event name..."
                        color={'secondary'}
                        value={search}
                        handleChange={evt => handleSearch(evt)}
                        handleSearch={(e) => { e?.preventDefault(); }}
                    />}
                </Grid>
                <Grid item xs={12} sm="auto">
                    {/* <Button className={classes.btn} variant="outlined">Export Calendar</Button> */}
                </Grid>
            </Grid>
        </>
    )
}

const ChangeColorFunction = () => {
    const ColorCode = 'rgb(' + (Math.floor(Math.random() * 256)) + ',' + (Math.floor(Math.random() * 256)) + ',' + (Math.floor(Math.random() * 256)) + ')';
    return ColorCode;
}

const EventContainer = ({ item }) => {
    const classes = useStyles();
    const month = new Date(item.start_date).toLocaleString('default', { month: 'long' })
    const date = new Date(item.start_date).getDate();
    const startDateTime = item.start_date + ' ' + item.start_time;
    const endDateTime = item.end_date + ' ' + item.end_time;
    const totalTime = calculateDuration(startDateTime, endDateTime);
    // const duration = calculateDuration(item.start_date + ' ' + item.start_time, item.end_date + ' ' + item.end_time);
    // const checkLaunchTime = checkCurrentEventTime(new Date(startDateTime), new Date(endDateTime));
    
    return (
        <Paper elevation={3} className={classes.paper}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2} className={classes.calendarView}>
                    <div className={classes.cal}>
                        <h6 style={{
                            padding: 10,
                            fontSize: 15,
                            borderTopLeftRadius: 10,
                            borderTopRightRadius: 10,
                            background: `${ChangeColorFunction()}`,
                            color: 'white',
                            margin: 0,
                        }}>{month}</h6>
                        <h5 className={classes.date}>{date}</h5>
                    </div>
                </Grid>
                <Grid item xs={12} sm={6} md={3} className={classes.intro}>
                    <h3>{item.name}</h3>
                    <div style={{ display: 'flex', margin: 0 }}>
                        <QueryBuilderIcon className={classes.icon} />
                        <p style={{ margin: 0, fontSize: 13, padding: 5 }}>{totalTime}</p>
                    </div>
                </Grid>
                <Grid item xs={12} md={4} className={classes.notes}>
                    <h3>Note</h3>
                    <p style={{ fontSize: 12, overflow: 'auto', width: '100%' }}>{item.note}</p>
                </Grid>
                <Grid item xs={12} sm={6} md={2} className={classes.notes}>
                    <h3>URL</h3>
                    {item.url !== "" ? (<a href={item.url} style={{ color: "#3D7DDD", textDecoration: 'none' }} rel="noreferrer" target="_blank">Event Link</a>) : "Not Available"}
                </Grid>
                {/* <Button style={{
                        color: '#3D7DDD',
                        border: '1px solid #3D7DDD',
                        padding: '5px 10px',
                        borderRadius: 25,
                        background: '#fff',
                        fontSize: 10
                    }}>Export</Button> */}
            </Grid>
        </Paper>
    )
}

function formatDateYYMMDD(date) {
    var d = new Date(date),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear();

    if (month.length < 2) 
        month = '0' + month;
    if (day.length < 2) 
        day = '0' + day;

    return [year, month, day].join('-');
}

function pad2(number) {
    return number = (number < 10 ? '0' : '') + number;
}

Date.prototype.addDays = function(days) {
    var date = new Date(this.valueOf());
    date.setDate(date.getDate() + days);
    return date;
}

// function getDatesFromStartEnd(startDate, stopDate) {
//     let dateArray = new Array();
//     let currentDate = new Date(startDate);
//     while (currentDate.getTime() <= new Date(stopDate).getTime()) {
//         dateArray.push(moment(new Date (currentDate)).format("DD-MM-YYYY"));
//         currentDate = currentDate.addDays(1);
//     }
//     return dateArray;
// }


function getWeeklyWeekdays(startDate, stopDate) {
    const result = [];
    const start = new Date(startDate);
    const end = new Date(stopDate);

    const targetDay = start.getDay(); // Day of week (0=Sun, 1=Mon, ..., 6=Sat)
    let current = new Date(start);

    while (current <= end) {
        if (current.getDay() === targetDay) {
            result.push(moment(current).format("DD-MM-YYYY"));
            current.setDate(current.getDate() + 7); // Move to next same weekday
        } else {
            current.setDate(current.getDate() + 1); // Align to correct day
        }
    }
    return result;
}

function generateMonthlyDates(startDate, endDate) {
    const result = [];
    let currentDate = new Date(startDate); // clone startDate
    const targetDay = currentDate.getDate();
  
    while (currentDate <= endDate) {
      const dd = String(currentDate.getDate()).padStart(2, '0');
      const mm = String(currentDate.getMonth() + 1).padStart(2, '0');
      const yyyy = currentDate.getFullYear();
      result.push(`${dd}-${mm}-${yyyy}`);
  
      // Move to the same day in the next month
      let nextMonth = new Date(currentDate);
      nextMonth.setMonth(currentDate.getMonth() + 1);
  
      // Handle cases like Feb 30 (nonexistent)
      if (nextMonth.getDate() !== targetDay) {
        nextMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0); // Last valid day
      }
  
      currentDate = nextMonth;
    }
  
    return result;
  }
  

function getRendomClass(){
    const items = ['evt-bg-color1', 'evt-bg-color2', 'evt-bg-color3', 'evt-bg-color4', 'evt-bg-color5'];
    return items[items.length * Math.random() | 0];
}

const Calendar = () => {

    const classes = useStyles();
    const commonClasses = commonStyles();
    const [state, setState] = React.useState({
        weekendsVisible: true,
        currentEvents: []
    });
    const [openEventModal, setOpenEventModal] = React.useState(false);
    const [calendarEvents, setCalendarEvents] = React.useState([]);
    const [selectedEventID, setSelectedEventID] = React.useState("");
    const [openSingleEvent, setOpenSingleEvent] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [eventList, setEventList] = React.useState([]);
    const [eventLoding, setEventLoding] = React.useState(false);
    const [sideBarEventList, setSideBarEventList] = React.useState([]);
    const [sideBarDate, setSideBarDate] = React.useState(new Date());
    const [showCalendar, setShowCalendar] = React.useState(true);
    const [currentDateInfo, setCurrentDateInfo] = React.useState(null);
    const [eventFilterDate, setEventFilterDate] = React.useState(new Date());

    React.useEffect(() => {
        setLoading(false);
        const crtMonth = eventFilterDate.getMonth()+1;
        getEventList(`?start_date__month=${pad2(crtMonth)}`);
        const query = `?start_date__gte=${formatDateYYMMDD(sideBarDate)}&end_date__lte=${formatDateYYMMDD(sideBarDate)}`;
        getSideBarEventList(query);
    }, []);

    const getCalanderEvents = async (query) => {
        const events = await calendarServices.getAllEvents(query);
        if (events.results && events.results.length) {
            setEventList(events.results);
            const eventCalendarList = events.results && events.results.length > 0 && events.results.map(item => {
                return {
                    id: item.uuid,
                    title: item.name,
                    start: moment(new Date(item.start_date)).format("YYYY-MM-DD") + 'T' + item.start_time,
                    end: moment(new Date(item.start_date)).format("YYYY-MM-DD") + 'T' + item.end_time,
                    className:[getRendomClass()],
                    description: item.note,
                }
            });
            setCalendarEvents(eventCalendarList);
        }
    }

    const getEventList = async (query) => {
        setEventLoding(true);
        const events = await calendarServices.getAllEvents(query);
        if (events) {
            setEventList(events?.results || []);
        }
        setEventLoding(false);
    }
    
    const getSideBarEventList = async (query) => {
        const events = await calendarServices.getAllEvents(query);
        if (events.results && events.results.length) {
            setSideBarEventList(events.results);
        }
    }

    const handleEventClick = (clickInfo) => {
        setSelectedEventID(clickInfo.event._def.publicId);
        setOpenSingleEvent(true);
    };

    const handleEvents = (events) => {
        setState({
            currentEvents: events
        });
    };

    const handleDateSelect = () => {
        
    };

    const handleSaveEvents = async (data, dateRange = null) => {
        const startTime = moment(new Date(data.from)).format('HH:mm');
        const endTime =  moment(new Date(data.to)).format('HH:mm');
        let payload = {
            name: data.title,
            start_time: startTime,
            end_time: endTime,
            repeat_mode: data.repeat_mode,
            main_type: data.type,
            event_type: data.eventType,
            url: data.url,
            note: data.note,
            project_id: data.project.id,
            description: data.note
        };
        if(["Weekly"].includes(data.repeat_mode) && dateRange){
            payload.start_date = getWeeklyWeekdays(dateRange?.startDate, dateRange?.endDate);
            payload.end_date = moment(new Date(data.expiryDate)).format("DD-MM-YYYY");
            payload.expiry_date = moment(new Date(data.expiryDate)).format("DD-MM-YYYY");
        }
        else if(["Monthly"].includes(data.repeat_mode) && dateRange){
            payload.start_date = generateMonthlyDates(dateRange?.startDate, dateRange?.endDate);
            payload.end_date = moment(new Date(data.expiryDate)).format("DD-MM-YYYY");
            payload.expiry_date = moment(new Date(data.expiryDate)).format("DD-MM-YYYY"); 
        }else{
            payload.start_date = data.startDate;
            payload.end_date = data.endDate;
            payload.expiry_date = data.endDate;
        }
        if (data.type === "Event") {
            if (data.eventType === "Assessment") {
                payload.assessment_activity = data.courseActivity
                payload.assessment_id = data.course.assessment_id
            } else if (data.eventType === "Apprenticeship") {
                payload.apprenticeship_activity = data.courseActivity
                payload.apprenticeship_id = data.course.apprenticeship_id
            } else {
                payload.course_activity = data.courseActivity;
                payload.course_id = data.course.course_id
            }
        }
        const result = await calendarServices.addEvent(payload, selectedEventID || null);
        if (result && result.status) {
            setOpenEventModal(false)
            notify("success", result.message)
            handleGetEventByMonth(currentDateInfo);
        }
        setLoading(false);
    }

    const handleSaveSingleEvent = async (eventData) => {
        return await handleSaveEvents(eventData);
    }

    const onClickMonth = () => {
        setShowCalendar(true);
    }

    const handleClickCalendarEvent = () => {
        setShowCalendar(false)
    }

    const handleSearch = (evt) => {
        setSearch(evt.target.value);
        const crtMonth = eventFilterDate.getMonth()+1;
        return getEventList(`?start_date__month=${pad2(crtMonth)}&name__icontains=${evt.target.value}`);
    }

    const handleAddEvent = () => {
        setOpenEventModal(true);
        setSelectedEventID("");
    }

    const handleGetEventByMonth = (dateInfo) => {
        setCurrentDateInfo({start: dateInfo.start, end: dateInfo.end});
        const query = `?limit=10000&start_date__gte=${formatDateYYMMDD(dateInfo.start)}&end_date__lte=${formatDateYYMMDD(dateInfo.end)}`;
        return getCalanderEvents(query);
    }

    return (
        <Layout>
            <main>
                <div className={`${commonClasses.contentWrapper} ${classes.contentWrapper}`}>
                    <SearchAndExport
                        search={search}
                        showCalendar={showCalendar}
                        handleSearch={handleSearch}
                    />
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8} className={classes.calendarMain}>
                            {showCalendar ? (
                                <div className={classes.fullCalendarWrapper}>
                                    <FullCalendar
                                        handleEventClick={handleEventClick}
                                        handleEvents={handleEvents}
                                        renderEventContent={renderEventContent}
                                        handleDateSelect={handleDateSelect}
                                        initialEvents={calendarEvents}
                                        weekendsVisible={true}
                                        handleClickCalendarEvent={handleClickCalendarEvent}
                                        handleGetEventByMonth={handleGetEventByMonth}
                                    />
                                </div>
                            ) : 
                            <>
                                <Header 
                                    handleDateChange={date => {
                                        setEventFilterDate(date);
                                        const crtMonth = new Date(date).getMonth()+1;
                                        return getEventList(`?start_date__month=${pad2(crtMonth)}`);
                                    }}
                                    selectedDate={eventFilterDate}
                                    onClickMonth={onClickMonth} 
                                />
                                <div className={classes.eventsListSection}>
                                    {(eventLoding) ? <div className={classes.eventRoot}>
                                        <Paper elevation={3} className={classes.paper}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} style={{textAlign: "center"}}>
                                                    <CircularProgress color="primary" size={20} />
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </div> : (eventList && eventList.length > 0) ? eventList.map(item => (
                                        <div className={classes.eventRoot}>
                                            <EventContainer item={item} />
                                        </div>
                                    )) : 
                                    <div className={classes.eventRoot}>
                                        <Paper elevation={3} className={classes.paper}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} className={classes.calendarView}>
                                                    <Typography variant="h4" style={{ fontSize: 'clamp(1.25rem, 4vw, 2rem)' }}>Events not found for this month!</Typography>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </div>
                                }
                                </div>
                            </>}
                        </Grid>
                        <Grid item xs={12} md={4} className={classes.sidebarWrap}>
                            <CalendarSidebar 
                                eventList={sideBarEventList} 
                                handleAddEvent={handleAddEvent} 
                                date={sideBarDate}
                                changeDate={(date) => {
                                    const query = `?start_date__gte=${formatDateYYMMDD(new Date(date))}&end_date__lte=${formatDateYYMMDD(new Date(date))}`;
                                    getSideBarEventList(query);
                                    setSideBarDate(date);
                                }} 
                            />
                        </Grid>
                    </Grid>
                </div>
            </main>
            <EventModal
                open={openEventModal}
                handleClose={evt => setOpenEventModal(false)}
                handleSaveEvents={handleSaveEvents}
                btnLoading={loading}
            />
            {selectedEventID && 
            <SingleEventModal
                open={openSingleEvent}
                handleClose={evt => {
                    setOpenSingleEvent(false);
                    setSelectedEventID("");
                }}
                handleSaveSingleEvent={handleSaveSingleEvent}
                id={selectedEventID}
            />}
        </Layout>
    );
}

export default Calendar;